import React, { Component, Fragment } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import ViewsContainer from '../../components/views/ViewsContainer';
import LoadingWizard from '../../components/spinner/wizard';
import RequestDetail from './requestDetail';
import moment from 'moment';

class BookingMeetings extends Component {
	constructor(props) {
		super(props);

		// Process initial base columns
		const { columns, columnTypes, toggledColumns, normalizedColumns } = this.processBaseColumns();

		// Process form field data if booking page has a form
		const { toggledFieldLabels, normalizedFieldLabels } = this.processFormFields(
			props.bookingPage,
			toggledColumns,
			normalizedColumns
		);

		// Process requests data
		const processedRequests = this.processRequestsData(props.bookingPage?.requests || []);

		// Create category columns for the ViewsContainer
		const fieldCategories = this.createCategoryColumns(normalizedFieldLabels);

		this.state = {
			columns,
			columnTypes,
			normalizedColumns,
			toggledColumns,
			normalizedFieldLabels,
			toggledFieldLabels,
			categoryColumns: fieldCategories,
			requests: processedRequests,
			sidePanelOpen: false,
			loading: false,
			activeRow: ''
		};
	}

	// Process base columns (similar to event submissions)
	processBaseColumns = () => {
		const columns = {
			customerEmail: 'Email',
			customerName: 'Customer Name',
			requestedSlots: 'Requested Slots',
			requestedCalendars: 'Requested Calendars',
			approvedSlots: 'Approved Slots',
			deniedSlots: 'Denied Slots',
			submittedAt: 'Submitted',
			status: 'Status'
		};

		const columnTypes = {
			customerEmail: 'email',
			customerName: 'string',
			requestedSlots: 'number',
			requestedCalendars: 'number',
			approvedSlots: 'number',
			deniedSlots: 'number',
			submittedAt: 'timestamp',
			status: 'string'
		};

		// Define column order explicitly
		const columnOrder = [
			'customerEmail',
			'customerName', 
			'requestedSlots',
			'requestedCalendars',
			'approvedSlots',
			'deniedSlots',
			'submittedAt',
			'status'
		];

		const toggledColumns = {};
		const normalizedColumns = columnOrder.map((cfc) => {
			toggledColumns[cfc] = true;
			return {
				label: columns[cfc],
				key: cfc,
				value: cfc,
				type: columnTypes[cfc],
				sortAsc: false,
				sortDesc: false,
			};
		});

		return { columns, columnTypes, toggledColumns, normalizedColumns };
	}

	// Process form fields from booking page form (similar to event submissions)
	processFormFields = (bookingPage, toggledColumns, normalizedColumns) => {
		const toggledFieldLabels = { ...toggledColumns };
		
		// Start with base columns but remove status columns
		const baseColumnsWithoutStatus = normalizedColumns.filter(col => 
			!['submittedAt', 'status'].includes(col.key)
		);
		const statusColumns = normalizedColumns.filter(col => 
			['submittedAt', 'status'].includes(col.key)
		);
		
		let normalizedFieldLabels = [...baseColumnsWithoutStatus];

		// Check if booking page has a form with pages
		if (bookingPage && bookingPage.form && bookingPage.form.pages) {
			bookingPage.form.pages.forEach((page) => {
				page.sections.forEach((section) => {
					section.fields.forEach((field) => {
						// Skip basic fields that are already handled
						if (!['firstName', 'lastName', 'email'].includes(field.id)) {
							toggledFieldLabels[field.id] = true;
							normalizedFieldLabels.push({
								key: field.id,
								label: field.label,
								type: field.type,
								value: field.id,
								sortAsc: false,
								sortDesc: false,
							});

							// Handle nested sections
							if (field.type === "nestedSections" || field.type === "groupSelection") {
								field.nestedSections?.forEach((nestedSection) => {
									nestedSection.questions?.forEach((question) => {
										toggledFieldLabels[question.id] = true;
										normalizedFieldLabels.push({
											key: question.id,
											label: question.label,
											type: question.type,
											value: question.id,
											sortAsc: false,
											sortDesc: false,
										});
									});
								});
							}
						}
					});
				});
			});
		}

		// Add status columns at the end
		normalizedFieldLabels = [...normalizedFieldLabels, ...statusColumns];

		return { toggledFieldLabels, normalizedFieldLabels };
	}

	// Create category columns for ViewsContainer
	createCategoryColumns = (normalizedFieldLabels) => {
		const baseColumns = [
			'customerEmail',
			'customerName',
			'requestedSlots',
			'approvedSlots',
			'deniedSlots'
		];

		const statusColumns = [
			'submittedAt',
			'status'
		];

		const formFieldColumns = normalizedFieldLabels
			.filter(field => ![...baseColumns, ...statusColumns].includes(field.key))
			.map(field => field.key);

		const categories = {
			basics: {
				title: 'Request Details',
				columns: baseColumns
			}
		};

		if (formFieldColumns.length > 0) {
			categories.formData = {
				title: 'Form Submission Data',
				columns: formFieldColumns
			};
		}

		categories.status = {
			title: 'Status Information',
			columns: statusColumns
		};

		return categories;
	}

	componentDidUpdate(prevProps) {
		// Reprocess data when requests change
		if (prevProps.bookingPage?.requests !== this.props.bookingPage?.requests) {
			const processedRequests = this.processRequestsData(this.props.bookingPage?.requests || []);
			this.setState({ requests: processedRequests });
		}

		// Reprocess form fields if booking page form changes
		if (prevProps.bookingPage?.form !== this.props.bookingPage?.form) {
			const { toggledFieldLabels, normalizedFieldLabels } = this.processFormFields(
				this.props.bookingPage,
				this.state.toggledColumns,
				this.state.normalizedColumns
			);
			const fieldCategories = this.createCategoryColumns(normalizedFieldLabels);
			
			this.setState({ 
				toggledFieldLabels, 
				normalizedFieldLabels,
				categoryColumns: fieldCategories
			});
		}
	}

	processRequestsData = (requests) => {
		return requests.map(request => {
			// Calculate total requested slots from selectedDateTimes
			let totalSlots = 0;
			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach(date => {
					Object.keys(request.selectedDateTimes[date]).forEach(calendarId => {
						totalSlots += request.selectedDateTimes[date][calendarId].length;
					});
				});
			}

			let approvedSlots = 0
			if (request.approvedTimes) {
				Object.keys(request.approvedTimes).forEach(date => {
					Object.keys(request.approvedTimes[date]).forEach(calendarId => {
						approvedSlots += request.approvedTimes[date][calendarId].length;
					});
				});
			}

			let deniedSlots = 0;
			if (request.deniedTimes) {
				Object.keys(request.deniedTimes).forEach(date => {
					Object.keys(request.deniedTimes[date]).forEach(calendarId => {
						deniedSlots += request.deniedTimes[date][calendarId].length;
					});
				});
			}

			// Process form submission data (similar to event submissions)
			let formData = {};
			if (request.submission && request.submission.formData) {
				// Handle extraFields if they exist
				let extraFields = {};
				if (request.submission.formData.extraFields) {
					try {
						extraFields = typeof request.submission.formData.extraFields === "string"
							? JSON.parse(request.submission.formData.extraFields)
							: request.submission.formData.extraFields;
					} catch (e) {
						extraFields = {};
					}
				}

				// Merge form data with extra fields
				formData = {
					...request.submission.formData,
					...extraFields
				};
			}

			if(formData.reservedDateTimes){
				let reservations = typeof formData.reservedDateTimes == "string" 
					? JSON.parse(formData.reservedDateTimes) 
					: formData.reservedDateTimes;
				
				if(Array.isArray(reservations)){
					formData.reservedDateTimes = reservations.map(reservation => 
						`Start Date: ${reservation.startDate} Start Time: ${reservation.startTime}\nEnd Date: ${reservation.endDate} End Time: ${reservation.endTime}`
					).join(',\n'); 
				}
			}

			return {
				...request,
				customerName: request.submission?.formData?.firstName && request.submission?.formData?.lastName 
					? `${request.submission.formData.firstName} ${request.submission.formData.lastName}`
					: request.submission?.formData?.firstName || 'Unknown Customer',
				customerEmail: request.submission?.formData?.email || '',
				requestedSlots: totalSlots,
				requestedCalendars: request.selectedCalendars ? request.selectedCalendars.length : 0,
				approvedSlots: approvedSlots,
				deniedSlots: deniedSlots,
				submittedAt: request.submission?.submittedAt || request.createdAt,
				status: approvedSlots + deniedSlots === totalSlots ? 'Reviewed' : 'Needs review',
				// Include all form data for filtering and display
				...formData
			};
		});
	};

	setActiveRow = (requestId) => {
		this.setState({ activeRow: requestId });
	};

	render() {
		const { bookingPage, orgId } = this.props;
		const { 
			categoryColumns,
			columns,
			columnTypes,
			normalizedFieldLabels,
			toggledFieldLabels,
			requests,
			sidePanelOpen,
			activeRow
		} = this.state;

		if (!bookingPage) {
			return <LoadingWizard loading={true} text="Loading booking page..." />;
		}

		return (
			<Switch>
				<Route path={`/${orgId}/booking/${bookingPage._id}/requests`}>
					<Fragment>
						<ViewsContainer
							activeRow={activeRow}
							categoryColumns={categoryColumns}
							views={[]} // No custom views for now
							entries={requests}
							columns={normalizedFieldLabels ? 
								normalizedFieldLabels.reduce((acc, field) => {
									acc[field.key] = field.label;
									return acc;
								}, {}) : columns
							}
							columnTypes={normalizedFieldLabels ? 
								normalizedFieldLabels.reduce((acc, field) => {
									acc[field.key] = field.type;
									return acc;
								}, {}) : columnTypes
							}
							title="Booking Requests"
							readOnly={false}
							mainActions={[]}
							batchActions={[]}
							enableDownloadViews={true}
							orgId={orgId}
							type={'BookingRequests'}
							referenceCollection={'BookingPages'}
							referenceId={bookingPage._id}
							addedEntries={[]}
							deletedEntries={[]}
							breadcrumbs={[
								{
									name: bookingPage.name || 'Booking Page',
								}
							]}
							columnWidths={{
								customerEmail: 250,
								customerName: 200,
								requestedSlots: 150,
								requestedCalendars: 180,
								approvedSlots: 150,
								deniedSlots: 150,
								submittedAt: 200,
								status: 150
							}}
							emptyStateTitle="No Booking Requests Yet"
							emptyStateDescription="When customers submit booking requests through your booking page, they will appear here for you to manage."
							emptyStateIcon="las la-calendar-check"
							
						/>

						<div className={`sidePanelContainer ${sidePanelOpen ? 'panelOpen' : ''}`}>
							<Switch>
								<Route path={`/:organization/booking/:bookingId/requests/:requestId`}
									render={(props) => {
										const requestId = props.match.params.requestId;
										if (!sidePanelOpen) this.setState({ sidePanelOpen: true });
										
										return (
											<div className='h-100' key={requestId}>
												<RequestDetail
													bookingPage={bookingPage}
													requests={requests}
													orgId={orgId}
													setActiveRow={this.setActiveRow}
													onApproveTimeSlot={this.props.approveRequestTimeSlot}
													onDenyTimeSlot={this.props.denyRequestTimeSlot}
												/>
											</div>
										);
									}}
								/>
								<Route exact path={`/:organization/booking/:bookingId/requests`}
									render={() => {
										if (sidePanelOpen) this.setState({ sidePanelOpen: false });
										return <></>;
									}}
								/>
							</Switch>
						</div>
					</Fragment>
				</Route>
			</Switch>
		);
	}
}

export default withRouter(BookingMeetings);
